import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Link as MuiLink,
  List,
  ListItem,
  SvgIcon,
  useTheme,
} from '@mui/material';
import { Description, Mail, CalendarMonth } from '@mui/icons-material';
import { headerGradient } from '../../theme';
import { fetchPublicContentModule } from '../../services/publicContentApi';
import type { SecadDocumentItem } from '../../types/content';

const fallbackDocuments: SecadDocumentItem[] = [
  {
    id: 'fallback-secad-1',
    title: 'Calendário Acadêmico 2025.2',
    href: encodeURI('/assets/docs/secad/Calendário Acadêmico 2025_2.pdf'),
    order: 1,
    isActive: true,
    storageType: 'legacy',
    resourceType: 'file',
  },
  {
    id: 'fallback-secad-2',
    title: 'Ordem de Serviço nº 007/2025 - Reajuste Valores Documentos Educacionais',
    href: encodeURI(
      '/assets/docs/secad/ORDEM DE SERVIÇO N.º 007.2025 -  REAJUSTE VALORES DOCUMENTOS EDUCACIONAIS- FATESE-Manifesto.pdf'
    ),
    order: 2,
    isActive: true,
    storageType: 'legacy',
    resourceType: 'file',
  },
];

function getDocumentIcon(title: string, index: number) {
  if (title.toLowerCase().includes('calend')) {
    return CalendarMonth;
  }

  return index === 0 ? CalendarMonth : Description;
}

const SecretariaAcademica: React.FC = () => {
  const theme = useTheme();
  const [documents, setDocuments] = useState<SecadDocumentItem[]>(fallbackDocuments);

  useEffect(() => {
    let isMounted = true;

    void fetchPublicContentModule<SecadDocumentItem>('secretariaSecadDocuments')
      .then((response) => {
        if (isMounted) {
          setDocuments(response.items || []);
        }
      })
      .catch((error) => {
        console.warn('Nao foi possivel carregar os documentos publicados da Secad. Mantendo fallback local.', error);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  return (
    <Box sx={{ pt: { xs: 6, md: 10 } }}>
      <Box sx={{ textAlign: 'center', mb: 4, px: { xs: 2, md: 6 } }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            position: 'relative',
            display: 'inline-block',
            pb: 1.5,
            letterSpacing: 0.2,
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              bottom: 0,
              width: 100,
              height: 4,
              backgroundColor: theme.palette.secondary.main,
              borderRadius: theme.shape.borderRadius,
              boxShadow: '0 2px 4px rgba(0,0,0,0.12)',
            },
          }}
        >
          Secretaria Acadêmica
        </Typography>
      </Box>

      <Box
        sx={{
          height: 6,
          width: '100vw',
          maxWidth: '100vw',
          ml: 'calc(50% - 50vw)',
          mr: 'calc(50% - 50vw)',
          background: headerGradient,
          mb: 4,
        }}
      />

      <Box sx={{ px: { xs: 2, md: 6 } }}>
        <Typography paragraph sx={{ textAlign: 'justify', textIndent: '2em', lineHeight: 1.8 }}>
          A Secretaria Acadêmica é o setor responsável pelo registro, controle e acompanhamento da vida acadêmica
          dos estudantes da Faculdade de Tecnologia Senac Amazonas. É por meio dela que você acessa e atualiza
          informações essenciais da sua trajetória no ensino superior. Entre os principais serviços prestados estão:
          matrícula, rematrícula, emissão de documentos, trancamento ou cancelamento de disciplinas e cursos, além de
          orientações sobre prazos, normas e procedimentos acadêmicos.
        </Typography>
        <Typography paragraph sx={{ textAlign: 'justify', textIndent: '2em', lineHeight: 1.8 }}>
          A Secretaria atua com base em critérios de transparência, agilidade e respeito ao estudante, sendo um ponto
          de apoio fundamental durante toda a sua jornada na Faculdade. Em caso de dúvidas ou solicitações, entre em
          contato pelos canais oficiais ou dirija-se à unidade presencialmente. Estamos aqui para garantir que sua
          experiência acadêmica seja organizada, segura e eficiente.
        </Typography>

        <Typography
          variant="h6"
          component="h2"
          sx={{
            fontWeight: 700,
            color: 'text.primary',
            mb: 2,
            pb: 1,
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: 80,
              height: 3,
              background: theme.palette.secondary.main,
              borderRadius: theme.shape.borderRadius,
            },
          }}
        >
          Documentos e Informações
        </Typography>

        {documents.length ? (
          <List sx={{ pl: 0 }}>
            {documents.map((document, index) => {
              const icon = getDocumentIcon(document.title, index);
              return (
                <ListItem key={document.id} sx={{ gap: 1.5, px: 0 }}>
                  <SvgIcon component={icon} sx={{ fontSize: '70px', color: 'primary.main' }} />
                  <MuiLink
                    href={document.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: 'primary.main', fontWeight: 600 }}
                  >
                    {document.title}
                  </MuiLink>
                </ListItem>
              );
            })}
          </List>
        ) : (
          <Typography color="text.secondary" sx={{ py: 1 }}>
            Nenhum documento publicado no momento.
          </Typography>
        )}

        <Typography sx={{ mt: 4, lineHeight: 1.8 }}>
          <SvgIcon component={Mail} sx={{ fontSize: '40px', color: 'secondary.main', mr: 1, verticalAlign: 'middle' }} />
          <strong>Contato:</strong>{' '}
          <MuiLink href="mailto:secretaria.faculdade@am.senac.br" underline="hover" sx={{ color: 'primary.main' }}>
            secretaria.faculdade@am.senac.br
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  );
};

export default SecretariaAcademica;
